import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from 'store';
import {
  ConlicitasegState,
  InsuranceQuote,
  InsuranceProcess,
  QuoteResponse,
} from 'store/modules/conlicitaseg_store/types';
import { useHistory, useParams } from 'react-router-dom';
import {
  formatCurrency,
  formatCNPJ,
  formatDate,
  getInsuranceReason,
  calculateCoverage,
  calculateEndDate,
  todayInBrt,
} from 'services/conlicitaseg';
import {
  setSelectedQuoteStore,
  clearCompanyData,
  getQuotes,
} from 'store/modules/conlicitaseg_store/actions';
import QuoteSelectionCards from '../quote_selection_cards';
import QuoteDownloadBlock from '../quote_download_block';

const QuotesSelection: React.FC = () => {
  const { processId } = useParams<{ processId: string }>();
  const [selectedProcess, setSelectedProcess] = useState<InsuranceProcess | undefined>(undefined);

  const history = useHistory();
  const { last_quotes, resume_process, processes } = useSelector<
    ApplicationState,
    ConlicitasegState
  >((state) => state.session.conlicitaseg);

  const contractGuaranteeValue = formatCurrency(
    calculateCoverage(
      selectedProcess?.contract_value || 0,
      selectedProcess?.contract_percentage || 0,
    ),
    { emptyOnZero: true },
  );

  const [selectedQuote, setSelectedQuote] = useState<string>('');

  const dispatch = useDispatch();

  const selectedQuoteData: InsuranceQuote | undefined = last_quotes?.find(
    (q: QuoteResponse) => q?.quote_response?.selected_api_quote_id === selectedQuote,
  )?.quote_response;

  const handleContinueClick = () => {
    if (!selectedQuoteData) {
      history.push('/conlicitaseg');
      return;
    }
    dispatch(setSelectedQuoteStore(selectedQuoteData));
    dispatch(clearCompanyData());
    history.push('/conlicitaseg/pagamento');
  };

  useEffect(() => {
    if (selectedProcess === undefined) return;
    dispatch(getQuotes());
  }, []);

  useEffect(() => {
    setSelectedProcess(processes.find((process) => process.id === Number(processId)));
  }, [processes]);

  if (selectedProcess === undefined) return <div />;

  const today = todayInBrt();

  const isLicitanteWithPastStart =
    selectedProcess.type_warranty_id === 'Licitante' &&
    !!selectedProcess.contract_start_date &&
    selectedProcess.contract_start_date < today;

  const displayStartDate = isLicitanteWithPastStart ? today : selectedProcess.contract_start_date;

  const displayEndDate = isLicitanteWithPastStart
    ? calculateEndDate(today, Number(selectedProcess.contract_days))
    : selectedProcess.contract_end_date;

  return (
    <div className="page-container">
      <div className="container-fluid big-resolutions py-5 px-md-5">
        <div className="row justify-content-center mt-5">
          <div className={resume_process ? 'col-12' : 'col-12 mx-auto'}>
            <button
              type="button"
              className="btn btn-link px-0 mb-3"
              onClick={() => history.push('/conlicitaseg')}
            >
              <i className="material-icons align-middle">keyboard_arrow_left</i> Voltar para a lista
              de cotações e apólices
            </button>
            <div className={resume_process ? 'row' : ''}>
              <div className={resume_process ? 'col-xl-8' : ''}>
                <div className="bg-white rounded shadow-sm p-4">
                  <h2 className="mb-1">Selecione a cotação desejada</h2>
                  {last_quotes.length !== 0 && (
                    <div className="text-secondary mb-4">
                      Cotações emitidas com cobertura adicional trabalhista. Sua cotação foi salva
                      automaticamente.
                    </div>
                  )}
                  <div className="row justify-content-center">
                    <QuoteSelectionCards
                      selectedQuote={selectedQuote}
                      setSelectedQuote={setSelectedQuote}
                    />
                  </div>
                  {selectedQuote !== '' && (
                    <QuoteDownloadBlock
                      className="d-none d-md-block"
                      inline
                      quote={selectedQuoteData}
                    />
                  )}
                  {last_quotes.length !== 0 && (
                    <div className="d-flex justify-content-end align-items-center mt-3">
                      <div className="font-italic mr-3">
                        <p className="text-secondary mb-0">Selecione uma cotação para continuar.</p>
                        <p className="text-dark">
                          <strong>
                            Antes de emitir a apólice, recomendamos que as condições e coberturas
                            sejam validadas pela unidade licitante. Vale observar que após a emissão
                            da apólice não será possível realizar o cancelamento do seguro garantia.{' '}
                          </strong>
                        </p>
                      </div>
                      <button
                        type="button"
                        className="btn btn-primary mr-3"
                        disabled={selectedQuote === ''}
                        onClick={handleContinueClick}
                      >
                        Continuar
                      </button>
                    </div>
                  )}
                </div>
              </div>
              {resume_process && (
                <div className="col-xl-4">
                  <div className="bg-white rounded shadow-sm p-4">
                    <h4 className="mb-3">Dados da cotação</h4>

                    <div className="mb-3">
                      <strong>Motivo do seguro</strong>
                      <div className="text-secondary">
                        {getInsuranceReason(selectedProcess?.type_warranty_id || '')}
                      </div>
                    </div>

                    <div className="mb-3">
                      <strong>Empresa que está contratando o seguro (Tomador)</strong>
                      <div className="text-secondary">
                        {formatCNPJ(selectedProcess?.policyholder_cnpj)} -{' '}
                        {selectedProcess?.policyholder_company_name}
                      </div>
                    </div>

                    <div className="mb-3">
                      <strong>Órgão público que solicitou o seguro (Segurado)</strong>
                      <div className="text-secondary">
                        {formatCNPJ(selectedProcess?.insured.cnpj)} -{' '}
                        {selectedProcess?.insured.name}
                      </div>
                      <div className="text-secondary">
                        {selectedProcess?.insured.address}, n°{selectedProcess?.insured.number} -{' '}
                        {selectedProcess?.insured.district}, {selectedProcess?.insured.city} -{' '}
                        {selectedProcess?.insured.state}
                      </div>
                    </div>

                    <div className="mb-3">
                      <strong>Modalidade da licitação</strong>
                      <div className="text-secondary">{selectedProcess?.bidding_modality}</div>
                    </div>

                    <div className="mb-3">
                      <strong>N° do edital</strong>
                      <div className="text-secondary">{selectedProcess?.bidding_edict}</div>
                    </div>

                    {selectedProcess?.bidding_contract_number && (
                      <div className="mb-3">
                        <strong>N° do contrato</strong>
                        <div className="text-secondary">
                          {selectedProcess?.bidding_contract_number}
                        </div>
                      </div>
                    )}

                    {selectedProcess?.bidding_process_number && (
                      <div className="mb-3">
                        <strong>N° do processo</strong>
                        <div className="text-secondary">
                          {selectedProcess?.bidding_process_number}
                        </div>
                      </div>
                    )}

                    {isLicitanteWithPastStart && (
                      <div className="alert alert-warning mb-3" role="alert">
                        As datas de início e fim do seguro foram alteradas para seguir as regras das
                        seguradoras.
                      </div>
                    )}

                    <div className="mb-3">
                      <strong>Vigência do seguro</strong>
                      <div className="text-secondary">
                        {formatDate(displayStartDate)} até {formatDate(displayEndDate)} (
                        {selectedProcess?.contract_days || 0} dias)
                      </div>
                    </div>

                    <div className="mb-3">
                      <strong>
                        {selectedProcess?.type_warranty_id === 'Licitante'
                          ? 'Valor estimado'
                          : 'Valor do contrato'}
                      </strong>
                      <div className="text-secondary">
                        {formatCurrency(selectedProcess?.contract_value || 0, {
                          emptyOnZero: true,
                        })}
                      </div>
                    </div>

                    <div className="mb-3">
                      <strong>Percentual da garantia</strong>
                      <div className="text-secondary">
                        {selectedProcess?.contract_percentage || 0}%
                      </div>
                    </div>

                    {selectedProcess?.contract_additional_value &&
                      selectedProcess?.contract_additional_value > 0 && (
                        <>
                          <div className="mb-3">
                            <strong>Valor da garantia contratual</strong>
                            <div className="text-secondary">{contractGuaranteeValue}</div>
                          </div>
                          <div className="mb-3">
                            <strong>Valor de garantia adicional</strong>
                            <div className="text-secondary">
                              {formatCurrency(selectedProcess?.contract_additional_value || 0, {
                                emptyOnZero: true,
                              })}
                            </div>
                          </div>
                        </>
                      )}

                    <div className="mb-3">
                      <strong>Valor total da cobertura</strong>
                      <div className="text-secondary">
                        {formatCurrency(selectedProcess?.contract_insured_amount || 0, {
                          emptyOnZero: true,
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuotesSelection;
